/**
 * useWeatherStream · v2.7.30 (2026-02-21)
 * --------------------------------------
 * Centralised React hook that subscribes the Admin Console / Driver app
 * / Rider app to the global weather-alert WS bus. Reacts to:
 *
 *   rtg:weather_alert  — push-worthy NWS Tornado / Severe T-storm /
 *                        Flash Flood / Civil Emergency / AMBER etc.
 *                        Fires EAS attention tones, toast banner, and
 *                        flips a global `activeAlert` state so any
 *                        consuming component can render a banner.
 *
 *   rtg:manual_warning — operator broadcast from the "Dispatch Warning"
 *                        console. Same shape — surfaced via toast.
 *
 * Anyone can mount this — it dedupes the listeners so multiple subscribers
 * don't compound the alarm tone.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { playWeatherAlarm, stopWeatherAlarm, isWeatherAlarmActive } from "@/lib/weatherAlarm";

// Module-level dedupe — only the first hook instance actually fires the
// alarm; later instances just receive the React state updates.
let _firstInstance = null;
let _recentAlertIds = new Set();

export default function useWeatherStream({ enabled = true, audibleAlarm = true } = {}) {
  const [activeAlert, setActiveAlert] = useState(null);
  const [recentManual, setRecentManual] = useState(null);

  const ackActive = useCallback(() => {
    setActiveAlert(null);
    stopWeatherAlarm();
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const myToken = Symbol("ws-instance");
    if (!_firstInstance) _firstInstance = myToken;

    const onWeather = (e) => {
      const detail = e.detail || {};
      // Dedupe — same alert_id should not double-fire even if multiple
      // WS messages arrive in quick succession.
      if (detail.alert_id && _recentAlertIds.has(detail.alert_id)) return;
      if (detail.alert_id) {
        _recentAlertIds.add(detail.alert_id);
        // Roll forgotten ids after 5 min
        setTimeout(() => _recentAlertIds.delete(detail.alert_id), 5 * 60 * 1000);
      }
      setActiveAlert(detail);
      // Only the FIRST hook instance plays the alarm so we don't stack
      // sine waves when multiple components mount.
      if (audibleAlarm && _firstInstance === myToken && detail.is_push) {
        if (!isWeatherAlarmActive()) playWeatherAlarm();
      }
      toast.error(
        `⚠ ${detail.event || "Weather Alert"} · ${detail.headline?.slice(0, 80) || ""}`,
        {
          duration: detail.is_push ? 12000 : 5000,
          id: `weather-${detail.alert_id || Date.now()}`,
        },
      );
    };

    const onManual = (e) => {
      const detail = e.detail || {};
      setRecentManual(detail);
      const sevToType = { critical: "error", warning: "warning", info: "info" };
      const fn = toast[sevToType[detail.severity] || "info"] || toast.info;
      fn(`📣 Dispatch Warning · ${detail.message?.slice(0, 100) || ""}`, {
        duration: detail.severity === "critical" ? 10000 : 5000,
      });
    };

    window.addEventListener("rtg:weather_alert", onWeather);
    window.addEventListener("rtg:manual_warning", onManual);
    return () => {
      window.removeEventListener("rtg:weather_alert", onWeather);
      window.removeEventListener("rtg:manual_warning", onManual);
      if (_firstInstance === myToken) _firstInstance = null;
    };
  }, [enabled, audibleAlarm]);

  return { activeAlert, recentManual, ackActive };
}
